// ─── Vacances scolaires (France) → garde par moitiés ──────────────
// Porté de MoPCal. Charge les vacances via l'API data.education.gouv.fr et
// découpe chaque période en 2 moitiés. La garde des vacances suit un SCHÉMA
// GLOBAL (première/deuxième moitié + alternance année impaire) commun à toutes
// les vacances, appliqué ou non PAR vacances (appliqueGarde). Comme pour les
// rythmes, la garde est CALCULÉE à l'affichage (jamais écrite en base) — seules
// les surcharges (échanges manuels) vivent dans jours/{dk}.
// Config stockée sur calendriers/{calId}.vacancesConfig. Données PARTAGÉES MoPCal.
import { db, frJMA } from '@mop/core';
import { doc, setDoc, getDoc } from 'firebase/firestore';

const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
// Décale une date-clé (YYYY-MM-DD) de n jours.
export const decaleJour = (dk, n) => { const [y, m, d] = dk.split('-').map(Number); return fmt(new Date(y, m - 1, d + n)); };

export const ZONES = ['Zone A', 'Zone B', 'Zone C', 'Corse'];

export function getAnneesDisponibles() {
  const now = new Date();
  const annee = now.getFullYear();
  const debut = now.getMonth() >= 8 ? annee : annee - 1;   // année scolaire commence en septembre
  return [debut - 1, debut, debut + 1, debut + 2].map(a => `${a}-${a + 1}`);
}

export function emojiVacances(nom) {
  const n = (nom || '').toLowerCase();
  if (n.includes('toussaint')) return '🍂';
  if (n.includes('noël') || n.includes('noel')) return '❄️';
  if (n.includes('hiver')) return '⛷️';
  if (n.includes('printemps')) return '🌸';
  if (n.includes('été') || n.includes('ete') || n.includes('grandes')) return '☀️';
  return '🏖️';
}

// Charge les périodes de vacances (zone + année scolaire) depuis l'API gouv.
export async function chargerVacancesGouvernement(zone, anneeScolaire) {
  const where = `zones="${zone}" AND annee_scolaire="${anneeScolaire}"`;
  const url = `https://data.education.gouv.fr/api/explore/v2.1/catalog/datasets/fr-en-calendrier-scolaire/records`
    + `?where=${encodeURIComponent(where)}&limit=100&order_by=start_date`;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Erreur API gouvernementale (${res.status})`);
  const json = await res.json();
  const seen = new Set(); const periodes = [];
  for (const r of (json.results || [])) {
    if (!r.start_date || !r.end_date) continue;
    if ((r.population || '').toLowerCase().includes('enseignant')) continue;
    const debutDate = new Date(r.start_date);
    const finDate = new Date(r.end_date); finDate.setDate(finDate.getDate() - 1);   // end = rentrée (exclusif)
    const debut = fmt(debutDate), fin = fmt(finDate);
    const key = `${r.description}|${debut}|${fin}`;
    if (!seen.has(key)) { seen.add(key); periodes.push({ nom: r.description || 'Vacances', debut, fin }); }
  }
  return periodes;
}

export async function sauvegarderVacancesConfig(calId, vacancesConfig) {
  await setDoc(doc(db, 'calendriers', calId), { vacancesConfig }, { merge: true });
}
export async function chargerVacancesConfig(calId) {
  const snap = await getDoc(doc(db, 'calendriers', calId));
  return snap.exists() ? (snap.data().vacancesConfig ?? null) : null;
}

// Découpe une période en 2 moitiés (floor puis reste).
export function calculerMoities(debut, fin) {
  const dD = new Date(debut + 'T00:00:00'), dF = new Date(fin + 'T00:00:00');
  const nb = Math.round((dF - dD) / 86400000) + 1;
  const nb1 = Math.floor(nb / 2);
  const finM1 = new Date(dD); finM1.setDate(finM1.getDate() + nb1 - 1);
  const debM2 = new Date(dD); debM2.setDate(debM2.getDate() + nb1);
  return { debut1: debut, fin1: fmt(finM1), debut2: fmt(debM2), fin2: fin, nbJours1: nb1, nbJours2: nb - nb1 };
}

// Répartition ½1/½2 d'une période selon le SCHÉMA GLOBAL.
// schema = { premiereMoitieGarde, deuxiemeMoitieGarde, alternerAnneeImpaire }.
// Années paires → schéma tel quel ; années impaires → inversé si l'alternance est
// activée. L'année est déduite de la date de début de la période.
export function moitiesGardePeriode(p, schema) {
  const {
    premiereMoitieGarde = 'parent1',
    deuxiemeMoitieGarde = 'parent2',
    alternerAnneeImpaire = false,
  } = schema || {};
  const annee = parseInt((p?.debut || '').slice(0, 4), 10) || 0;
  const inverse = alternerAnneeImpaire && (annee % 2 === 1);
  return {
    moitie1Garde: inverse ? deuxiemeMoitieGarde : premiereMoitieGarde,
    moitie2Garde: inverse ? premiereMoitieGarde : deuxiemeMoitieGarde,
  };
}

// ── Garde des vacances : TIMELINE de schémas-périodes (comme les rythmes) ──
// Chaque « garde vacances » couvre une période { debut, fin|null } et porte son
// schéma { premiereMoitieGarde, deuxiemeMoitieGarde, alternerAnneeImpaire,
// debutVeille } + un drapeau `applique`. Stockée dans vacancesConfig.gardeVacances.
export const frDateV = (s) => frJMA(s);
const trierV = (l) => [...(l || [])].sort((a, b) => (a.debut < b.debut ? -1 : a.debut > b.debut ? 1 : 0));

// Schéma-période « depuis toujours » construit depuis l'ancien schéma global (rétro-compat).
function schemaGlobalHerite(c) {
  const p1 = c.premiereMoitieGarde || 'parent1';
  return {
    debut: '2000-01-01', fin: null,
    premiereMoitieGarde: p1,
    deuxiemeMoitieGarde: c.deuxiemeMoitieGarde || (p1 === 'parent2' ? 'parent1' : 'parent2'),
    alternerAnneeImpaire: !!c.alternerAnneeImpaire,
    debutVeille: c.debutVeille !== false,
    applique: true,
  };
}

// Liste des schémas-périodes (rétro-compat : ancien schéma global → 1 période).
export function lireGardeVacances(cfg) {
  const c = cfg || {};
  if (Array.isArray(c.gardeVacances) && c.gardeVacances.length) return trierV(c.gardeVacances);
  return [schemaGlobalHerite(c)];
}

// Schéma-période ACTIVE (appliquée) couvrant un jour donné, ou null.
export function schemaVacancesPour(cfg, dk) {
  return lireGardeVacances(cfg).filter(s => s.applique !== false)
    .find(s => s.debut <= dk && (!s.fin || dk <= s.fin)) || null;
}

// ── Opérations sur la timeline (pures) — mêmes règles que les rythmes ──
export function insererGardeVac(liste, schema) {
  const l = trierV(liste).map(p => ({ ...p }));
  const debut = schema.debut;
  if (l.some(p => p.debut === debut)) return { ok: false, err: 'garde.errDejaDate' };
  const idxContenante = l.findIndex(p => p.debut < debut && (!p.fin || debut <= p.fin));
  if (idxContenante >= 0) {
    const c = l[idxContenante];
    const nouvelle = { ...schema, fin: c.fin || null };
    c.fin = decaleJour(debut, -1);
    return { ok: true, liste: trierV([...l, nouvelle]) };
  }
  const suivante = l.find(p => p.debut > debut) || null;
  const nouvelle = { ...schema, fin: suivante ? decaleJour(suivante.debut, -1) : null };
  return { ok: true, liste: trierV([...l, nouvelle]) };
}

export function editerGardeVac(liste, index, champs) {
  const l = trierV(liste).map(p => ({ ...p }));
  const p = l[index];
  if (!p) return { ok: false, err: 'garde.errIntrouvable' };
  const prec = l[index - 1], suiv = l[index + 1];
  const d = champs.debut || p.debut;
  if (prec && d <= prec.debut) return { ok: false, err: 'garde.errGvApresPrec', errP: { date: frDateV(prec.debut) } };
  if (suiv && d >= suiv.debut) return { ok: false, err: 'garde.errGvAvantSuiv', errP: { date: frDateV(suiv.debut) } };
  const nf = (champs.fin === undefined) ? (p.fin || null) : (champs.fin || null);
  if (nf) {
    if (nf < d) return { ok: false, err: 'garde.errFinApresDebut' };
    if (suiv && nf >= suiv.debut) return { ok: false, err: 'garde.errGvFinAvantSuiv', errP: { date: frDateV(suiv.debut) } };
  } else if (suiv) {
    return { ok: false, err: 'garde.errGvSansFinImpossible' };
  }
  Object.assign(p, champs, { debut: d, fin: nf });
  if (prec) prec.fin = decaleJour(d, -1);
  return { ok: true, liste: trierV(l) };
}

export function supprimerGardeVac(liste, index) {
  const l = trierV(liste).map(p => ({ ...p }));
  const enleve = l[index];
  if (!enleve) return l;
  l.splice(index, 1);
  if (l.length === 0) return l;
  if (index > 0) l[index - 1].fin = (index >= l.length) ? null : enleve.fin;
  return l;
}

// Garde CALCULÉE d'un jour : schéma-période active ce jour → répartition ½ de la
// vacances couvrant le jour. Aucune écriture en base. Veille au soir gérée par
// le schéma actif (debutVeille).
export function gardeVacancesJour(dk, vacancesConfig) {
  const cfg = vacancesConfig || {};
  const schema = schemaVacancesPour(cfg, dk);
  if (!schema) return null;
  const veilleActive = schema.debutVeille !== false;
  const periodes = Array.isArray(cfg.periodes) ? cfg.periodes : [];
  const p = periodes.find(v => {
    if (v.actif === false || v.appliqueGarde === false || !v.debut || !v.fin) return false;
    const debutEff = veilleActive ? decaleJour(v.debut, -1) : v.debut;
    return debutEff <= dk && dk <= v.fin;
  });
  if (!p) return null;
  const { moitie1Garde, moitie2Garde } = moitiesGardePeriode(p, schema);
  if (veilleActive && dk === decaleJour(p.debut, -1)) return moitie1Garde;   // veille au soir
  const { fin1 } = calculerMoities(p.debut, p.fin);
  return dk <= fin1 ? moitie1Garde : moitie2Garde;
}
